import { useState, useEffect, useCallback } from 'react';
import { useWeightSimulation } from './useWeightSimulation';

// Helper function to check if we're in Electron environment
const isElectronEnvironment = () => {
	return window.electronAPI && window.electronAPI.isElectron;
};

// Helper functions for secure storage with localStorage fallback
const secureStorage = {
	async getSettings() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.getSettings();
		} else {
			const stored = localStorage.getItem('truckscale_settings');
			return stored ? JSON.parse(stored) : null;
		}
	},
	
	async setSettings(settings) {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.setSettings(settings);
		} else {
			localStorage.setItem('truckscale_settings', JSON.stringify(settings));
			return true;
		}
	}
};

export const useDevices = () => {
	const [devices, setDevices] = useState({
		printer: { connected: false, name: 'Not configured' },
		scale: { connected: false, weight: 0 },
		cameras: []
	});

	const [config, setConfig] = useState({
		printer: '',
		paperWidth: '80mm',
		scaleDevice: '',
		cameras: []
	});

	const [simulationInterval, setSimulationInterval] = useState(null);

	// Use callback to prevent stale closures
	const handleScaleConnectionChanged = useCallback((event, data) => {
		// console.log('📡 Scale connection changed:', data);
		setDevices(prev => {
			let deviceIdentifier;
			
			// Handle different scale types
			if (data.type === 'serial') {
				deviceIdentifier = data.connected ? `serial_${data.port}` : prev.scale.device;
			} else {
				// USB scale
				deviceIdentifier = data.connected ? `${data.vendorId}_${data.productId}` : prev.scale.device;
			}
			
			const newState = {
				...prev,
				scale: {
					...prev.scale,
					connected: data.connected,
					device: deviceIdentifier,
					type: data.type || 'usb'
				}
			};
			// console.log('📡 New devices state after update:', newState);
			return newState;
		});
	}, []);

	// Use callback for scale reconnection handler
	const handleScaleReconnected = useCallback((event, data) => {
		// console.log('🔄 Scale reconnected:', data);
		setDevices(prev => {
			let deviceIdentifier;
			
			// Handle different scale types
			if (data.type === 'serial') {
				deviceIdentifier = `serial_${data.port}`;
			} else {
				// USB scale
				deviceIdentifier = `${data.vendorId}_${data.productId}`;
			}
			
			return {
				...prev,
				scale: {
					...prev.scale,
					connected: true,
					device: deviceIdentifier,
					type: data.type || 'usb'
				}
			};
		});
		
		// Track scale reconnection in Raygun
		const trackingData = data.type === 'serial' 
			? { port: data.port, type: data.type, timestamp: data.timestamp }
			: { vendorId: data.vendorId, productId: data.productId, timestamp: data.timestamp };
	}, []);

	// Separate useEffect for event listeners with proper cleanup
	useEffect(() => {
		if (!isElectronEnvironment() || !window.electronAPI?.onMessage) {
			return;
		}

		// console.log('🔧 Setting up scale event listeners');
		
		// Set up event listeners
		window.electronAPI.onMessage('scale-connection-changed', handleScaleConnectionChanged);
		window.electronAPI.onMessage('scale-reconnected', handleScaleReconnected);

		// Cleanup function
		return () => {
			// console.log('🧹 Cleaning up scale event listeners');
			if (window.electronAPI?.removeAllListeners) {
				window.electronAPI.removeAllListeners('scale-connection-changed');
				window.electronAPI.removeAllListeners('scale-reconnected');
			}
		};
	}, [handleScaleConnectionChanged, handleScaleReconnected]);

	// Add this to your useDevices hook
	useEffect(() => {
		if (config.scaleType && (config.scaleDevice || config.serialPort) && !devices.scale.connected) {
			// console.log('🔧 useDevices: Auto-connecting scale...');
			const timeoutId = setTimeout(() => {
				connectDevice('scale');
			}, 1000);
			
			return () => clearTimeout(timeoutId);
		}
	}, [config.scaleType, config.scaleDevice, config.serialPort, devices.scale.connected]);

	// Load saved configuration on component mount
	useEffect(() => {
		const loadSavedConfig = async () => {
			try {
				// console.log('🔧 Loading saved device configuration...');
				const savedSettings = await secureStorage.getSettings();
				
				if (savedSettings && savedSettings.deviceConfig) {
					// console.log('🔧 Found saved configuration:', savedSettings.deviceConfig);
					setConfig(savedSettings.deviceConfig);
					
					// Auto-connect devices if they were previously configured
					if (savedSettings.deviceConfig.printer) {
						// console.log('🔧 Auto-connecting printer...');
						setDevices(prev => ({
							...prev,
							printer: {
								connected: true,
								name: savedSettings.deviceConfig.printer,
								paperWidth: savedSettings.deviceConfig.paperWidth || '80mm'
							}
						}));
					}
					
					// Auto-connect scale if it was previously configured
					if (savedSettings.deviceConfig.scaleDevice) {
						// console.log('🔧 Auto-connecting scale...');
						
						setDevices(prev => ({
							...prev,
							scale: {
								connected: false, // Will be updated by connection logic
								weight: 0,
								device: savedSettings.deviceConfig.scaleDevice || 'simulation',
								type: savedSettings.deviceConfig.scaleType || 'usb'
							}
						}));
						
						// Handle simulation mode
						if (savedSettings.deviceConfig.scaleType === 'simulation') {
							setTimeout(() => {
								connectDevice('scale');
							}, 1000);
						}

						// Handle different scale types
						if (savedSettings.deviceConfig.scaleType === 'serial') {
							// Serial scale auto-connect
							if (window.scaleAPI && window.scaleAPI.openSerialScale) {
								// console.log('🔧 Setting up serial scale connection with delay...');
								setTimeout(() => {
									// console.log('🔧 Attempting serial scale connection now...');
									const serialConfig = {
										port: savedSettings.deviceConfig.serialPort,
										baudRate: savedSettings.deviceConfig.baudRate || 9600,
										dataBits: savedSettings.deviceConfig.dataBits || 8,
										parity: savedSettings.deviceConfig.parity || 'none',
										stopBits: savedSettings.deviceConfig.stopBits || 1,
										flowControl: savedSettings.deviceConfig.flowControl || 'none'
									};
									
									window.scaleAPI.openSerialScale(serialConfig)
										.then((result) => {
											// console.log('🔧 Serial scale connection result:', result);
											if (result) {
												// console.log('✅ Serial scale auto-connection successful');
											} else {
												// console.log('❌ Serial scale auto-connection failed - device not available');
											}
										})
										.catch((error) => {
											console.error('❌ Serial scale auto-connection error:', error);
										});
								}, 1500);
							}
						} else {
							// USB scale auto-connect (existing logic)
							const deviceIds = savedSettings.deviceConfig.scaleDevice.split('_');
							if (deviceIds.length === 2) {
								const vendorId = parseInt(deviceIds[0]);
								const productId = parseInt(deviceIds[1]);
								
								// console.log(`🔧 Attempting to connect to USB scale: vendorId=${vendorId}, productId=${productId}`);
								
								// Connect to the scale device with a delay to ensure event listeners are ready
								if (window.scaleAPI && window.scaleAPI.openScale) {
									// console.log('🔧 Setting up USB scale connection with delay...');
									setTimeout(() => {
										// console.log('🔧 Attempting USB scale connection now...');
										window.scaleAPI.openScale(vendorId, productId)
											.then((result) => {
												// console.log('🔧 USB scale connection result:', result);
												if (result) {
													// console.log('✅ USB scale auto-connection successful');
												} else {
													// console.log('❌ USB scale auto-connection failed - device not available');
												}
											})
											.catch((error) => {
												console.error('❌ USB scale auto-connection error:', error);
											});
									}, 1500); // Longer delay to ensure event listeners are set up
								}
							} else {
								console.warn('⚠️ Invalid USB scale device ID format:', savedSettings.deviceConfig.scaleDevice);
							}
						}
					}
					
					if (savedSettings.deviceConfig.cameras && savedSettings.deviceConfig.cameras.length > 0) {
						// console.log('🔧 Auto-connecting cameras...');
						setDevices(prev => ({
							...prev,
							cameras: savedSettings.deviceConfig.cameras
						}));
					}
				} else {
					// console.log('🔧 No saved configuration found, using defaults');
				}
			} catch (error) {
				console.error('❌ Failed to load saved configuration:', error);
			}
		};

		// Load config immediately since event listeners are now set up separately
		loadSavedConfig();
		
		// Cleanup function to disconnect devices when component unmounts
		return () => {
			// Clear simulation interval
			if (simulationInterval) {
				clearInterval(simulationInterval);
			}
			
			// Close scale connection on cleanup
			if (devices.scale.connected && window.scaleAPI && window.scaleAPI.closeScale) {
				// console.log('🔧 Cleaning up scale connection...');
				window.scaleAPI.closeScale();
			}
		};
	}, []); // Only run once on mount

	// Use weight simulation hook
	const simulatedWeight = useWeightSimulation(devices.scale.connected);

	// Update scale weight when simulation changes
	useEffect(() => {
		if (devices.scale.connected && devices.scale.type === 'simulation') {
			setDevices(prev => ({
				...prev,
				scale: { ...prev.scale, weight: simulatedWeight }
			}));
		}
	}, [simulatedWeight, devices.scale.connected, devices.scale.type]);

	const connectDevice = (deviceType, deviceConfig) => {
		return new Promise(async (resolve) => {
			try {
				switch (deviceType) {
					case 'printer':
						setDevices(prev => ({
							...prev,
							printer: {
								connected: true,
								name: deviceConfig.name || config.printer,
								paperWidth: config.paperWidth
							}
						}));
						break;

					case 'scale':
						// Close any existing scale connections
						if (window.scaleAPI && window.scaleAPI.closeScale) {
							await window.scaleAPI.closeScale();
						}
						
						if (window.scaleAPI && window.scaleAPI.closeSerialScale) {
							await window.scaleAPI.closeSerialScale();
						}
						
						// Clear any existing simulation
						if (simulationInterval) {
							clearInterval(simulationInterval);
							setSimulationInterval(null);
						}
						
						// Wait a moment for cleanup
						await new Promise(resolve => setTimeout(resolve, 500));

						// Handle simulation mode
						if (config.scaleType === 'simulation' || (config.dualScaleMode && (config.emptyScaleType === 'simulation' || config.fullScaleType === 'simulation'))) {
							// console.log('🔧 Starting scale simulation mode');
							
							setDevices(prev => ({
								...prev,
								scale: {
									connected: true,
									weight: 0,
									device: 'simulation',
									type: 'simulation'
								}
							}));
							
							// Start weight simulation with varying values
							const interval = setInterval(() => {
								// Generate realistic weight variations
								const baseWeight = 25; // Base weight in tons
								const variation = (Math.random() - 0.5) * 10; // ±5 tons variation
								const randomWeight = Math.max(0, baseWeight + variation);
								
								setDevices(prev => ({
									...prev,
									scale: { 
										...prev.scale, 
										weight: parseFloat(randomWeight.toFixed(2))
									}
								}));
							}, 5000); // Update every 2 seconds
							
							setSimulationInterval(interval);
							// console.log('✅ Scale simulation started');
							
						} else if (config.scaleType === 'serial' && !config.dualScaleMode) {
							// Serial scale connection
							// console.log(`🔧 Connecting to serial scale on port: ${config.serialPort}`);
							
							if (window.scaleAPI && window.scaleAPI.openSerialScale) {
								const serialConfig = {
									port: config.serialPort,
									baudRate: config.baudRate || 9600,
									dataBits: config.dataBits || 8,
									parity: config.parity || 'none',
									stopBits: config.stopBits || 1,
									flowControl: config.flowControl || 'none'
								};
								
								const result = await window.scaleAPI.openSerialScale(serialConfig);
								if (result) {
									// console.log('✅ Serial scale connection successful');
									// Don't set connected state here - let IPC event handle it
								} else {
									console.warn('⚠️ Serial scale connection failed');
									setDevices(prev => ({
										...prev,
										scale: {
											connected: false,
											weight: 0,
											device: `serial_${config.serialPort}`,
											type: 'serial'
										}
									}));
								}
							} else {
								console.warn('⚠️ Serial Scale API not available');
								// Fallback to simulation
								setDevices(prev => ({
									...prev,
									scale: {
										connected: true,
										weight: Math.floor(Math.random() * 50000) + 10000,
										device: `serial_${config.serialPort}`,
										type: 'serial'
									}
								}));
							}
						} else if (!config.dualScaleMode) {
							// USB scale connection (existing logic)
							const deviceIds = config.scaleDevice.split('_');
							if (deviceIds.length === 2) {
								const vendorId = parseInt(deviceIds[0]);
								const productId = parseInt(deviceIds[1]);
								
								// console.log(`🔧 Connecting to USB scale: vendorId=${vendorId}, productId=${productId}`);
								
								if (window.scaleAPI && window.scaleAPI.openScale) {
									const result = await window.scaleAPI.openScale(vendorId, productId);
									if (result) {
										// console.log('✅ USB scale connection successful');
										// Don't set connected state here - let IPC event handle it
									} else {
										console.warn('⚠️ USB scale connection failed');
										setDevices(prev => ({
											...prev,
											scale: {
												connected: false,
												weight: 0,
												device: config.scaleDevice,
												type: 'usb'
											}
										}));
									}
								} else {
									console.warn('⚠️ USB Scale API not available');
									// Fallback to simulation
									setDevices(prev => ({
										...prev,
										scale: {
											connected: true,
											weight: Math.floor(Math.random() * 50000) + 10000,
											device: config.scaleDevice,
											type: 'usb'
										}
									}));
								}
							} else {
								console.warn('⚠️ Invalid USB scale device ID format:', config.scaleDevice);
								setDevices(prev => ({
									...prev,
									scale: {
										connected: false,
										weight: 0,
										device: config.scaleDevice,
										type: 'usb'
									}
								}));
							}
						}
						break;

					case 'cameras':
						setDevices(prev => ({
							...prev,
							cameras: deviceConfig
						}));
						break;

					default:
						break;
				}
				resolve();
			} catch (error) {
				console.error('❌ Device connection error:', error);
				resolve();
			}
		});
	};

	const disconnectDevice = (deviceType) => {
		switch (deviceType) {
			case 'printer':
				setDevices(prev => ({
					...prev,
					printer: { connected: false, name: 'Not configured' }
				}));
				break;

			case 'scale':
				// Clear simulation interval if it exists
				if (simulationInterval) {
					clearInterval(simulationInterval);
					setSimulationInterval(null);
				}
				
				// Close the physical scale connection based on type
				if (devices.scale.type === 'serial') {
					if (window.scaleAPI && window.scaleAPI.closeSerialScale) {
						window.scaleAPI.closeSerialScale();
					}
				} else if (devices.scale.type === 'usb') {
					if (window.scaleAPI && window.scaleAPI.closeScale) {
						window.scaleAPI.closeScale();
					}
				}
				
				setDevices(prev => ({
					...prev,
					scale: { connected: false, weight: 0 }
				}));
				break;

			case 'cameras':
				setDevices(prev => ({
					...prev,
					cameras: []
				}));
				break;

			default:
				break;
		}
	};

	const testDevice = async (deviceType) => {
		switch (deviceType) {
			case 'printer':
				// Simulate test print
				return new Promise((resolve) => {
					setTimeout(() => {
						// console.log('Test print sent to printer');
						resolve({ success: true, message: 'Test print successful' });
					}, 1000);
				});

			case 'scale':
				// Simulate scale reading
				const testWeight = Math.floor(Math.random() * 50000) + 10000;
				setDevices(prev => ({
					...prev,
					scale: { ...prev.scale, weight: testWeight }
				}));
				return { success: true, weight: testWeight };

			case 'camera':
				// Simulate camera test
				return new Promise((resolve) => {
					setTimeout(() => {
						resolve({ success: true, message: 'Camera feed active' });
					}, 500);
				});

			default:
				return { success: false, message: 'Unknown device type' };
		}
	};

	// Enhanced setConfig that automatically saves to persistent storage
	const setConfigAndSave = async (newConfig) => {
		try {
			// console.log('🔧 Saving device configuration...');
			setConfig(newConfig);
			
			// Save to persistent storage
			await secureStorage.setSettings({
				deviceConfig: newConfig,
				lastUpdated: new Date().toISOString()
			});
			
			// console.log('🔧 Configuration saved successfully');
		} catch (error) {
			console.error('❌ Failed to save configuration:', error);
		}
	};

	return {
		devices,
		config,
		setConfig: setConfigAndSave,
		connectDevice,
		disconnectDevice,
		testDevice
	};
};